import "@tanstack/react-table";

import { type Dispatch, type SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Box, Button, Flex, MantineTheme, Paper, Stack, Sx } from "@mantine/core";
import {
    type ColumnDef,
    type ColumnFiltersState,
    ColumnOrderState,
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    OnChangeFn,
    type PaginationState,
    type Row,
    type RowData,
    RowSelectionState,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { useContextMenu } from "mantine-contextmenu";

export { createColumnHelper };

import { Link } from "react-router-dom";
import classNames from "classnames";
import { RoutePath } from "src/routes";
import { buildUrl, RoutePathParams } from "src/utils/buildUrl";

import { ContextMenuItem } from "../ContextMenu";
import { DnDHeader } from "./DndHeader";
import { MobileCard } from "./MobileCard";
import TableBody from "./TableBody";
import { ActionButtonType } from "./TableRow/TableRow";

import styles from "./Table.module.scss";

// Meta properties of table columns.
declare module "@tanstack/table-core" {
    interface ColumnMeta<TData extends RowData, TValue> {
        // filter?: {
        //     type: FilteringType;
        //     options?: RadioGroupOptions;
        // };
        align?: React.ComponentProps<"td">["align"];
        //The onRowClick callback will not be triggered when a cell is clicked (e.g., to prevent accidental clicks on action cells)
        disableRowClick?: boolean;
    }
}

// Meta properties of the table.
declare module "@tanstack/table-core" {
    interface TableMeta<TData extends RowData> {
        totalCount: number;
        // allRowsSelected: CheckboxState;
        // setAllRowsSelected: Dispatch<SetStateAction<CheckboxState>>;
        rowSelection: Record<string, boolean>;
        setRowSelection: Dispatch<SetStateAction<Record<string, boolean>>>;
        getIsRowSelectionDisabled?: (row: Row<any>) => boolean;
    }
}

export interface DataGridProps<Data> {
    // Column definitions
    columns: ColumnDef<Data, any>[];
    // Data that has been filtered or paginated, displaying only the visible subset.
    data: Data[];
    // For navigation: returns the URL to navigate to when row is clicked
    getRowHref?: (row: Row<Data>) => RoutePathParams<RoutePath>;
    // For non-navigation actions: callback function called whenever a row is clicked, unless disableRowClick is set to true in ColumnMeta.
    onRowClick?: (row: Row<Data>) => void;
    onRowDoubleClickCapture?: (row: Row<Data>) => void;
    // Callback function to apply column and row styles. Returns an array of class names
    // (e.g., predefined classes like CellTextColor, CellBackgroundColor, CellTextFormat, etc.).
    getCellStyles?: ({ row, column }: { row: Row<Data>; column: { id: any } }) => string[];
    getRowId: (originalRow: Data) => string;
    setPagination?: Dispatch<SetStateAction<PaginationState>>;
    globalFilter?: string;
    setGlobalFilter?: Dispatch<SetStateAction<string>>;
    columnFilters?: ColumnFiltersState;
    setColumnFilters?: Dispatch<SetStateAction<ColumnFiltersState>>;
    // rowSelection?: Record<string, any>;
    // setRowSelection?: Dispatch<SetStateAction<Record<string, boolean>>>;
    // getIsRowSelectionDisabled?: (row: Row<Data>) => boolean;
    isLoading?: boolean;
    mobileHeaderIndex?: number;
    secMobileHeaderIndex?: number;

    tableLayout?: "auto" | "fixed";

    enableRowSelection?: boolean;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;

    getContextMenuItems?(row: Row<Data>): ContextMenuItem[];
    actions?: ActionButtonType<Data>[];
}

const actionsSx: Sx = (theme: MantineTheme) => ({
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[3],
});

export const DataGrid = <Data extends object>({
    columns,
    data,
    globalFilter,
    setGlobalFilter,
    getRowHref,
    onRowClick,
    onRowDoubleClickCapture,
    getCellStyles,
    getRowId,
    isLoading,
    tableLayout,
    mobileHeaderIndex,
    secMobileHeaderIndex,
    enableRowSelection,
    rowSelection,
    onRowSelectionChange,
    getContextMenuItems,
    actions,
}: DataGridProps<Data>) => {
    const showContextMenu = useContextMenu();

    // Set column widths initially on page 1 to prevent resizing on subsequent pages
    const [columnWidths, setColumnWidths] = useState<number[]>([]);
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
        columns.map((column) => column.id as string) // must start out with populated columnOrder so we can splice
    );
    const [tableScrollOffsetX, setTableScrollOffsetX] = useState<number>(0);
    const [actionColumnWidth, setActionColumnWidth] = useState<number>(0);
    const tableWrapperRef = useRef<HTMLTableElement>(null);
    const actionColumnRef = useRef<HTMLDivElement>(null);

    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnOrder,
            globalFilter,
            rowSelection: rowSelection || {},
        },
        enableRowSelection,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnOrderChange: setColumnOrder,
        onGlobalFilterChange: setGlobalFilter,
        onRowSelectionChange: onRowSelectionChange,
        getRowId,
        debugTable: true,
    });

    useLayoutEffect(() => {
        if (!tableWrapperRef.current) {
            return;
        }

        const handleResize = () => {
            if (!tableWrapperRef.current) {
                return;
            }

            const headerCells = tableWrapperRef.current.querySelectorAll("th");
            setColumnWidths([...headerCells].map((cell) => cell.offsetWidth));
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [tableWrapperRef]);

    useEffect(() => {
        const tableWrapper = tableWrapperRef.current;
        if (!tableWrapper) {
            return;
        }

        const calculateTableScrollOffset = () => {
            const table = tableWrapper;
            const tableScrollOffsetX = table.scrollLeft;

            setTableScrollOffsetX(tableScrollOffsetX);
        };

        calculateTableScrollOffset();
        tableWrapper.addEventListener("scroll", calculateTableScrollOffset);

        return () => {
            if (!tableWrapper) {
                return;
            }

            tableWrapper.removeEventListener("scroll", calculateTableScrollOffset);
        };
    }, [tableWrapperRef]);

    const actionColumnStyle = useMemo(() => {
        return {
            transform: `translateX(${tableScrollOffsetX}px)`,
        };
    }, [tableScrollOffsetX]);

    const tableWrapperStyle = useMemo(() => {
        return {
            paddingRight: `${actionColumnWidth}px`,
        };
    }, [actionColumnWidth]);

    const rows = table.getRowModel().rows;

    useLayoutEffect(() => {
        if (!actionColumnRef.current || !actions?.length || !rows?.length) {
            return;
        }

        const actionColumnWidth = actionColumnRef.current.clientWidth;
        setActionColumnWidth(actionColumnWidth);
    }, [actionColumnRef, actions, rows]);

    return (
        <>
            <Paper className={styles.tableWrapper} radius="lg" ref={tableWrapperRef} style={tableWrapperStyle}>
                <table
                    className={styles.table}
                    style={{
                        tableLayout: tableLayout ? tableLayout : columnWidths.length ? "fixed" : "auto",
                    }}
                >
                    <thead className={styles.thead}>
                        {table.getHeaderGroups().map((headerGroup, index) => (
                            <Box key={`${index}${headerGroup.id}`} className={styles.tr} component="tr">
                                {headerGroup.headers.map((header, index) => (
                                    // Apply the header cell props
                                    <DnDHeader
                                        key={header.id}
                                        header={header}
                                        table={table}
                                        columnWidths={columnWidths}
                                        index={index}
                                    />
                                ))}
                            </Box>
                        ))}
                    </thead>
                    <TableBody
                        rows={rows}
                        isLoading={isLoading}
                        getRowHref={getRowHref}
                        onRowClick={onRowClick}
                        onRowDoubleClickCapture={onRowDoubleClickCapture}
                        getCellStyles={getCellStyles}
                        getContextMenuItems={getContextMenuItems}
                        showContextMenu={showContextMenu}
                    />
                </table>

                {actions && (
                    <Stack className={styles.actionColumn} style={actionColumnStyle} ref={actionColumnRef}>
                        {rows.map((row) => (
                            <Flex key={row.id} className={classNames(styles.actionIconWrapper)} sx={actionsSx}>
                                {actions.map((action) => (
                                    <Flex key={row.id} align="center" justify="center">
                                        {action.type === "button" ? (
                                            <Button
                                                className={styles.actionIconButton}
                                                variant="light"
                                                color="gray"
                                                size="xs"
                                                onClick={() => action.onClick(row.original)}
                                            >
                                                <Stack spacing={4} align="center" justify="center">
                                                    {action.icon}
                                                    <span>{action.label}</span>
                                                </Stack>
                                            </Button>
                                        ) : (
                                            <Link
                                                to={buildUrl(
                                                    action.getRoutePathParams(row.original).path,
                                                    action.getRoutePathParams(row.original).params
                                                )}
                                                className={styles.actionIcon}
                                            >
                                                <Button
                                                    className={styles.actionIconButton}
                                                    component="div"
                                                    variant="light"
                                                    color="gray"
                                                    size="xs"
                                                >
                                                    <Stack spacing={4} align="center" justify="center">
                                                        {action.icon}
                                                        <span>{action.label}</span>
                                                    </Stack>
                                                </Button>
                                            </Link>
                                        )}
                                    </Flex>
                                ))}
                            </Flex>
                        ))}
                    </Stack>
                )}
            </Paper>
            <div className={styles.cards_wrapper}>
                {table.getRowModel().rows.map((row) => (
                    <MobileCard
                        key={row.id}
                        columns={columns}
                        row={row}
                        headerColIndex={mobileHeaderIndex}
                        secHeaderColIndex={secMobileHeaderIndex}
                    />
                ))}
            </div>
        </>
    );
};
